import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BillingHistoryDoc } from "../../Services/Billing";
import { isCancelledBillingDoc } from "../../utils/billingStatus";
import previewEditIcon from "../../assets/images&icons/01_preview edit.png";
import downloadIcon from "../../assets/images&icons/02_download.png";
import sendIcon from "../../assets/images&icons/03_send.png";
import sendTestIcon from "../../assets/images&icons/04_send test.png";
import addPaymentIcon from "../../assets/images&icons/05_add payment.png";
import cancelIcon from "../../assets/images&icons/06_cancel.png";

const ACTION_BUTTON_CLASS =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40";

const ICON_WRAP_CLASS =
  "inline-flex h-[18px] w-[18px] items-center justify-center";

function ActionIcon({ src }: { src: string }): React.ReactElement {
  return (
    <span className={ICON_WRAP_CLASS}>
      <img src={src} alt="" className="h-full w-full object-contain" />
    </span>
  );
}

const TOOLTIP_GAP = 8;
const TOOLTIP_MIN_SPACE = 44;

type TooltipPlacement = "top" | "bottom";

type TooltipCoords = {
  top: number;
  left: number;
  placement: TooltipPlacement;
};

function ActionTooltip({
  label,
  detail,
  children,
}: {
  label: string;
  detail?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: TooltipPlacement =
      spaceBelow < TOOLTIP_MIN_SPACE ? "top" : "bottom";

    setCoords({
      top:
        placement === "bottom"
          ? rect.bottom + TOOLTIP_GAP
          : rect.top - TOOLTIP_GAP,
      left: rect.right,
      placement,
    });
  }, []);

  const show = useCallback(() => {
    updatePosition();
    setOpen(true);
  }, [updatePosition]);

  const hide = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {open && coords
        ? createPortal(
            <div
              role="tooltip"
              className={`pointer-events-none fixed z-[9999] ${detail ? "max-w-[240px]" : "whitespace-nowrap"}`}
              style={{
                top: coords.placement === "bottom" ? coords.top : undefined,
                bottom:
                  coords.placement === "top"
                    ? window.innerHeight - coords.top
                    : undefined,
                left: coords.left,
                transform: "translateX(-100%)",
              }}
            >
              <div className="relative rounded bg-gray-font px-3 py-2 text-sm text-neutral shadow-lg">
                {detail ? (
                  <>
                    <div className="font-medium">{label}</div>
                    <div className="mt-0.5 text-neutral/90">{detail}</div>
                  </>
                ) : (
                  label
                )}
                {coords.placement === "bottom" ? (
                  <div className="absolute bottom-full right-3 h-0 w-0 border-x-4 border-b-4 border-transparent border-b-gray-font" />
                ) : (
                  <div className="absolute top-full right-3 h-0 w-0 border-x-4 border-t-4 border-transparent border-t-gray-font" />
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

type InvoiceActionKey =
  | "previewEdit"
  | "download"
  | "send"
  | "sendTest"
  | "addPayment"
  | "cancel";

type InvoiceAction = {
  key: InvoiceActionKey;
  label: string;
  icon: React.ReactElement;
};

const PREVIEW_EDIT_ACTION: InvoiceAction = {
  key: "previewEdit",
  label: "Preview & Edit",
  icon: <ActionIcon src={previewEditIcon} />,
};

const DOWNLOAD_ACTION: InvoiceAction = {
  key: "download",
  label: "Download",
  icon: <ActionIcon src={downloadIcon} />,
};

const SEND_ACTION: InvoiceAction = {
  key: "send",
  label: "Send",
  icon: <ActionIcon src={sendIcon} />,
};

const SEND_TEST_ACTION: InvoiceAction = {
  key: "sendTest",
  label: "Send Test",
  icon: <ActionIcon src={sendTestIcon} />,
};

const ADD_PAYMENT_ACTION: InvoiceAction = {
  key: "addPayment",
  label: "Add Payment",
  icon: <ActionIcon src={addPaymentIcon} />,
};

const CANCEL_ACTION: InvoiceAction = {
  key: "cancel",
  label: "Cancel",
  icon: <ActionIcon src={cancelIcon} />,
};

const PAYMENT_PREVIEW_EDIT_ACTION: InvoiceAction = {
  ...PREVIEW_EDIT_ACTION,
  label: "Preview & Edit Receipt",
};

const PAYMENT_DOWNLOAD_ACTION: InvoiceAction = {
  ...DOWNLOAD_ACTION,
  label: "Download Receipt",
};

const PAYMENT_SEND_ACTION: InvoiceAction = {
  ...SEND_ACTION,
  label: "Send Receipt",
};

const PAYMENT_SEND_TEST_ACTION: InvoiceAction = {
  ...SEND_TEST_ACTION,
  label: "Send Test Receipt",
};

const PAYMENT_CANCEL_ACTION: InvoiceAction = {
  ...CANCEL_ACTION,
  label: "Cancel Payment",
};

const PAYMENT_ACTIONS: InvoiceAction[] = [
  PAYMENT_PREVIEW_EDIT_ACTION,
  PAYMENT_DOWNLOAD_ACTION,
  PAYMENT_SEND_ACTION,
  PAYMENT_SEND_TEST_ACTION,
  PAYMENT_CANCEL_ACTION,
];

const INVOICE_ACTIONS: InvoiceAction[] = [
  PREVIEW_EDIT_ACTION,
  DOWNLOAD_ACTION,
  SEND_ACTION,
  SEND_TEST_ACTION,
  ADD_PAYMENT_ACTION,
  CANCEL_ACTION,
];

function isPaymentRow(row: BillingHistoryDoc & { type?: string; ledgerType?: string }): boolean {
  return row.type === "payment" || row.ledgerType === "Payment";
}

function isActionDisabled(
  action: InvoiceAction,
  row: BillingHistoryDoc & { type?: string; ledgerType?: string },
  downloading?: boolean,
  canSendBilling?: boolean,
): boolean {
  const isPayment = isPaymentRow(row);
  const isCancelled = isCancelledBillingDoc(row);
  const isInvoiceCancelled =
    !isPayment && row.type === "invoice" && isCancelled;
  const isPaymentCancelled = isPayment && isCancelled;

  if (action.key === "download" && (downloading || isCancelled)) {
    return true;
  }

  if (
    action.key === "send" &&
    canSendBilling === false &&
    !isCancelled
  ) {
    return true;
  }

  if (isPayment) {
    if (action.key === "cancel") {
      return row.isSent === true || isPaymentCancelled;
    }
    if (
      isPaymentCancelled &&
      (action.key === "send" || action.key === "sendTest")
    ) {
      return true;
    }
    return false;
  }

  if (action.key === "cancel") {
    return row.isSent === true || isInvoiceCancelled;
  }
  if (
    isInvoiceCancelled &&
    (action.key === "send" ||
      action.key === "sendTest" ||
      action.key === "addPayment")
  ) {
    return true;
  }

  return false;
}

function isSendBlockedByBillingContacts(
  action: InvoiceAction,
  row: BillingHistoryDoc & { type?: string; ledgerType?: string },
  canSendBilling?: boolean,
): boolean {
  return (
    action.key === "send" &&
    canSendBilling === false &&
    !isCancelledBillingDoc(row)
  );
}

function getActionAriaLabel(
  action: InvoiceAction,
  sendBillingDisabledReason?: string | null,
  sendBlockedByContacts?: boolean,
): string {
  if (sendBlockedByContacts && sendBillingDisabledReason) {
    return `${action.label}: ${sendBillingDisabledReason}`;
  }
  return action.label;
}

export default function AccountingInvoiceActionsCell({
  row,
  onPreviewEdit,
  onDownload,
  onSend,
  onSendTest,
  onAddPayment,
  onCancel,
  onPreviewEditPayment,
  onDownloadPayment,
  onSendPayment,
  onSendTestPayment,
  downloading,
  canSendBilling = true,
  sendBillingDisabledReason = null,
}: {
  row: BillingHistoryDoc & { type?: string; ledgerType?: string };
  onPreviewEdit?: (invoice: BillingHistoryDoc) => void;
  onDownload?: (invoice: BillingHistoryDoc) => void;
  onSend?: (invoice: BillingHistoryDoc) => void;
  onSendTest?: (invoice: BillingHistoryDoc) => void;
  onAddPayment?: (invoice: BillingHistoryDoc) => void;
  onCancel?: (invoice: BillingHistoryDoc) => void;
  onPreviewEditPayment?: (payment: BillingHistoryDoc) => void;
  onDownloadPayment?: (payment: BillingHistoryDoc) => void;
  onSendPayment?: (payment: BillingHistoryDoc) => void;
  onSendTestPayment?: (payment: BillingHistoryDoc) => void;
  downloading?: boolean;
  canSendBilling?: boolean;
  sendBillingDisabledReason?: string | null;
}): React.ReactElement {
  const isPayment = isPaymentRow(row);
  const actions = isPayment ? PAYMENT_ACTIONS : INVOICE_ACTIONS;

  return (
    <div
      className="flex flex-wrap items-center justify-start gap-0.5"
      role="group"
      aria-label={isPayment ? "Payment actions" : "Invoice actions"}
    >
      {actions.map((action) => {
        const sendBlockedByContacts = isSendBlockedByBillingContacts(
          action,
          row,
          canSendBilling,
        );
        const tooltipDetail =
          sendBlockedByContacts && sendBillingDisabledReason
            ? sendBillingDisabledReason
            : undefined;

        return (
          <ActionTooltip
            key={action.key}
            label={action.label}
            detail={tooltipDetail}
          >
            <button
              type="button"
              className={ACTION_BUTTON_CLASS}
              aria-label={getActionAriaLabel(
                action,
                sendBillingDisabledReason,
                sendBlockedByContacts,
              )}
              onClick={() => {
                if (action.key === "previewEdit") {
                  if (isPayment) {
                    onPreviewEditPayment?.(row);
                  }
                  else {
                    onPreviewEdit?.(row);
                  }
                  return;
                }
                if (action.key === "download") {
                  if (isPayment) {
                    onDownloadPayment?.(row);
                  }
                  else {
                    onDownload?.(row);
                  }
                  return;
                }
                if (action.key === "send") {
                  if (isPayment) {
                    onSendPayment?.(row);
                  }
                  else {
                    onSend?.(row);
                  }
                  return;
                }
                if (action.key === "sendTest") {
                  if (isPayment) {
                    onSendTestPayment?.(row);
                  }
                  else {
                    onSendTest?.(row);
                  }
                  return;
                }
                if (action.key === "addPayment") {
                  onAddPayment?.(row);
                  return;
                }
                if (action.key === "cancel") {
                  onCancel?.(row);
                }
              }}
              disabled={isActionDisabled(action, row, downloading, canSendBilling)}
            >
              {action.icon}
            </button>
          </ActionTooltip>
        );
      })}
    </div>
  );
}
